/**
 * What a keypress means on the queue.
 *
 * Pulled out of the effect so it can be tested without a DOM. The bindings
 * are advertised in two places — the context menu on a row and the help
 * panel — so getting them wrong is not a silent matter of taste: the product
 * tells people these keys work.
 */
export type QueueKeyAction = 'down' | 'up' | 'peek' | 'open' | 'close' | null;

/** Fields where a keystroke is text, not a command. */
function isTyping(el: Element | null): boolean {
  if (!el) return false;
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return true;
  // A rich text field is a DIV. Without this, typing "o" in one navigated
  // away mid-sentence.
  return (el as HTMLElement).isContentEditable === true;
}

export function queueKeyAction(e: {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  target?: unknown;
}): QueueKeyAction {
  if (isTyping((e.target as Element) ?? null)) return null;
  // Shift is NOT excluded: see the case note below.
  if (e.metaKey || e.ctrlKey || e.altKey) return null;

  /**
   * LOWER-CASED, which the original comparison was not.
   *
   * `e.key` is the character produced, so Caps Lock or a held Shift gives
   * "O", and `=== 'o'` missed it. Both places this is advertised print the
   * key as a capital — the context menu shows `O` and the help panel shows
   * `J / K` — so somebody reading either and pressing the key they were shown
   * got nothing at all.
   */
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;

  if (k === 'j' || k === 'ArrowDown') return 'down';
  if (k === 'k' || k === 'ArrowUp') return 'up';
  if (k === 'Enter') return 'peek';
  if (k === 'o') return 'open';
  if (k === 'Escape') return 'close';
  return null;
}

/**
 * Escape on the full ticket page: back to where you came from (AG-2).
 *
 * O opens the full ticket from the queue, and the queue's help says Escape
 * closes it. On the side panel it did; on the full page nothing listened, so
 * the same key that closed one view of a ticket did nothing on the other.
 *
 * Not while typing (Escape in the reply box is the editor's), and not while
 * an overlay is open: the dialog or menu takes that Escape, and leaving the
 * page with it would throw away whatever the dialog was for.
 */
export function ticketPageEscape(e: {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  defaultPrevented?: boolean;
  target?: unknown;
  overlayOpen?: boolean;
}): boolean {
  if (e.key !== 'Escape') return false;
  if (e.defaultPrevented || e.overlayOpen) return false;
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return false;
  return !isTyping((e.target as Element) ?? null);
}
